import { useMemo, useState } from 'react';

import { Grid } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import { ConfirmationModal } from 'src/components/Modals';
import StatusChip from 'src/components/StatusChip';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { INPROGRESS_MERCHANT_APPLICATION_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { DELETE_MERCHANT_APPLICATION } from 'src/graphql/mutations/deleteMerchantApplication.mutation';
import { GET_APPLICATIONS } from 'src/graphql/queries/merchantApplications.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { enumToCamelCase } from 'src/lib/formatters';
import {
    FilterOptionsType,
    filteredApplicationsTableData,
    updateTableFilters,
} from 'src/utils/tableUtils';

const MerchantApplicationsPage = () => {
    const [tableFilters, setTableFilters] = useState({});
    const { isAdmin } = useAuthContext();

    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        isOpen: false,
        id: null,
        merchantName: null,
    });

    const { data, loading, refetch } = useQuery(GET_APPLICATIONS, {
        fetchPolicy: 'cache-and-network',
        onError: () => showErrorToast('Problem loading merchant applications'),
    });
    const [deleteMerchantApplication] = useMutation(DELETE_MERCHANT_APPLICATION, {
        onCompleted: () => {
            setDeleteConfirmModal({
                isOpen: false,
                id: null,
                merchantName: null,
            });

            showSuccessToast('Merchant Application Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting Merchant Application');
        },
    });
    const handleDeleteApplication = () => {
        deleteMerchantApplication({
            variables: {
                applicationId: deleteConfirmModal.id,
            },
        });
    };
    const filterOptions = () => {
        const merchantApplications = data?.retrieveMerchantApplications?.data ?? [];
        return Object.fromEntries([
            [
                'Partner Name',
                Array.from(
                    new Set(
                        merchantApplications.map(({ partnerName }) => partnerName).filter(Boolean)
                    )
                ).map((partnerName) => ({
                    value: partnerName,
                    label: partnerName,
                })),
            ],
            [
                'Merchant Name',
                Array.from(
                    new Set(
                        merchantApplications.map(({ merchantName }) => merchantName).filter(Boolean)
                    )
                ).map((merchantName) => ({
                    value: merchantName,
                    label: merchantName,
                })),
            ],
            [
                'Status',
                Array.from(
                    new Set(merchantApplications.map(({ status }) => status).filter(Boolean))
                ).map((status) => {
                    const statusText = enumToCamelCase(status);
                    return {
                        value: statusText,
                        label: statusText,
                    };
                }),
            ],
            [
                'Worldpay PO',
                Array.from(
                    new Set(
                        merchantApplications.map(({ worldpayPO }) => worldpayPO).filter(Boolean)
                    )
                ).map((worldpayPO) => ({
                    value: worldpayPO,
                    label: worldpayPO,
                })),
            ],
        ]) as FilterOptionsType;
    };

    const merchantApplicationData = useMemo(() => {
        const originalApplications = data?.retrieveMerchantApplications?.data ?? [];
        const applicationData = originalApplications.map((application) => {
            const status = enumToCamelCase(application.status);
            return { ...application, status };
        });
        const updatedData = {
            ...data,
            retrieveMerchantApplications: {
                ...data?.retrieveMerchantApplications,
                data: applicationData,
            },
        };

        return filteredApplicationsTableData(updatedData, tableFilters).map((application) => ({
            ...application,
            status: <StatusChip status={application.status} />,
        }));
    }, [data, tableFilters]);

    const handleAddRecord = () => {
        navigate('/merchant/add');
    };

    const handleViewMerchantApplication = ({ id }) => {
        navigate(`/merchant-applications/view-application/${id}`);
    };

    return (
        <PageContent
            heading="In Progress Merchant Applications"
            subHeading="View and Update In Progress Merchant Applications as necessary"
        >
            <MetaTags
                title="In Progress Merchant Applications"
                description="In Progress Merchant Applications"
            />
            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Merchant"
                            data={merchantApplicationData}
                            headers={INPROGRESS_MERCHANT_APPLICATION_HEADERS}
                            title="In Progress Merchant Application List"
                            addRecordCallBack={handleAddRecord}
                            handleViewRecord={handleViewMerchantApplication}
                            deleteRecordCallBack={(id) => {
                                const { merchantName } = merchantApplicationData.find(
                                    (app) => app.id === id
                                );
                                setDeleteConfirmModal({ isOpen: true, id, merchantName });
                            }}
                            enableDelete={isAdmin}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) =>
                                setTableFilters((previousFilters) =>
                                    updateTableFilters(previousFilters, newFilterValue, filter)
                                )
                            }
                        />
                    )}
                </Grid>
            </Grid>
            <ConfirmationModal
                isOpen={deleteConfirmModal.isOpen}
                title="Delete Application?"
                cancelBtnVariant="back-button"
                dialogActionMarginTop={'14px'}
                message={
                    <>
                        Are you sure you want to permanently delete application ID{' '}
                        {deleteConfirmModal.id} for {deleteConfirmModal.merchantName}? <br /> The
                        application cannot be restored once deleted.
                    </>
                }
                onCancel={() => {
                    setDeleteConfirmModal({
                        isOpen: false,
                        id: null,
                        merchantName: null,
                    });
                }}
                onConfirm={handleDeleteApplication}
            />
        </PageContent>
    );
};

export default MerchantApplicationsPage;
